import { FC } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';

// components
import { Carousel } from 'dibs-carousel';
import { Mosaic } from 'dibs-mosaic/exports/Mosaic';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';

import { SharedRespAuctionItemTile } from './SharedRespAuctionItemTile';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';

// helpers
import { useClientState } from 'dibs-react-hooks/src/useClientState';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { spacingNumbers as sassySpacingNumbers } from 'dibs-sassy/exports/spacing';
import { trackArrowClick } from './similarAuctionTracking';

// styles
import styles from './SharedRespAuctionMosaicCarousel.scss';

// types
import { SharedRespAuctionMosaicCarousel_items$key } from './__generated__/SharedRespAuctionMosaicCarousel_items.graphql';
import { SharedRespAuctionMosaicCarousel_user$key } from './__generated__/SharedRespAuctionMosaicCarousel_user.graphql';

type Props = {
    items: SharedRespAuctionMosaicCarousel_items$key | null;
    user: SharedRespAuctionMosaicCarousel_user$key | null;
    favoritesLocation: string;
    trackingCategory: string;
    isWhiteBackground?: boolean;
};

const itemsFragment = graphql`
    fragment SharedRespAuctionMosaicCarousel_items on Item @relay(plural: true) {
        ...SharedRespAuctionItemTile_item
        ...similarAuctionTracking_item
        serviceId
    }
`;

const userFragment = graphql`
    fragment SharedRespAuctionMosaicCarousel_user on User {
        ...SharedRespAuctionItemTile_user
        serviceId
    }
`;

export const MOSAIC_CONFIG = {
    itemsPerPage: 6,
    layout: `
        i0 i0 i1 i3 i3 i4
        i0 i0 i2 i3 i3 i5
    `,
};

export const AUCTION_MOSAIC_ROW_HEIGHT = 140;

const SharedRespAuctionMosaicCarousel: FC<Props> = ({
    items: itemsRef,
    user: userRef,
    favoritesLocation,
    trackingCategory,
    isWhiteBackground,
}) => {
    const environment = useRelayEnvironment();
    const isClient = useClientState();
    const items = useFragment(itemsFragment, itemsRef);
    const user = useFragment(userFragment, userRef);

    const totalItems = items?.length || 0;
    const pages = Math.ceil(totalItems / MOSAIC_CONFIG.itemsPerPage);

    const renderMosaic = ({
        carouselIndex,
        favoritesProps,
        currency,
    }: {
        carouselIndex: number;
        favoritesProps: $TSFixMe; // FavoritesProvider still in jsx
        currency: string;
    }): JSX.Element => (
        <Mosaic
            layout={MOSAIC_CONFIG.layout}
            renderTile={key => {
                // get index from key
                const itemIndex =
                    carouselIndex * MOSAIC_CONFIG.itemsPerPage + Number(key.replace(/\D/g, '')); // convert i1 to 1
                const item = items?.[itemIndex];
                if (!item) {
                    return null;
                }
                return (
                    <SharedRespAuctionItemTile
                        user={user}
                        item={item}
                        favoritesProps={favoritesProps}
                        index={itemIndex}
                        trackingCategory={trackingCategory}
                        currency={currency}
                        isWhiteBackground={isWhiteBackground}
                    />
                );
            }}
            gap={sassySpacingNumbers.sassySpacingSmall}
            rowHeight={AUCTION_MOSAIC_ROW_HEIGHT}
        />
    );

    return (
        <FavoritesProvider
            userId={user?.serviceId}
            itemIds={(items || []).map(item => item.serviceId || null).filter(filterNulls)}
            disable={!isClient}
            environment={environment}
            fetchFolder={false}
            location={favoritesLocation}
        >
            {/* FavoritesProvider still in jsx */}
            {({ props: favoritesProps }: $TSFixMe) => (
                <CurrencyProvider
                    render={({ currency }: { currency: string }) => (
                        <Carousel
                            classNames={{
                                wrapper: styles.wrapper,
                                arrowLeft: styles.arrowLeft,
                                arrowRight: styles.arrowRight,
                            }}
                            totalItems={pages}
                            renderItem={({ index }: { index: number }) =>
                                renderMosaic({ carouselIndex: index, favoritesProps, currency })
                            }
                            onLeftArrowClick={() => trackArrowClick('left', trackingCategory)}
                            onRightArrowClick={() => trackArrowClick('right', trackingCategory)}
                        />
                    )}
                />
            )}
        </FavoritesProvider>
    );
};

export default SharedRespAuctionMosaicCarousel;
